import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import moment from 'moment'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import FormRenderer from './components/FormRenderer'
import ScoreSummaryCard from './components/ScoreSummaryCard'
import useOnlineStatus from 'hooks/useOnlineStatus'
import * as taskService from 'services/taskService'
import { toast } from 'react-toastify'
import { taskStatusMeta } from 'features/taskManagement/taskStatusConstants'
import { PRIORITY_META } from 'features/taskManagement/taskPriority'

const EXTERNAL_STATUS_LABEL = {
  awaiting_approval: 'Submitted',
}

const priorityConfig = {
  low: { label: PRIORITY_META.low.label, color: PRIORITY_META.low.fg },
  medium: { label: PRIORITY_META.medium.label, color: PRIORITY_META.medium.fg },
  high: { label: PRIORITY_META.high.label, color: PRIORITY_META.high.fg },
  critical: { label: PRIORITY_META.critical.label, color: PRIORITY_META.critical.fg },
}

const toSnakeCase = str => str.replace(/[A-Z]/g, l => `_${l.toLowerCase()}`)

const snakeCaseKeys = obj => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj
  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    const sk = toSnakeCase(key)
    if (Array.isArray(value)) {
      result[sk] = value.map(entry =>
        entry && typeof entry === 'object' && !Array.isArray(entry)
          ? snakeCaseKeys(entry)
          : entry
      )
    } else {
      result[sk] = value
    }
  }
  return result
}

const normalizeExternalTask = (taskData) => {
  if (!taskData?.formResponse?.responses) return taskData
  return {
    ...taskData,
    formResponse: {
      ...taskData.formResponse,
      responses: snakeCaseKeys(taskData.formResponse.responses)
    }
  }
}

const ExternalTaskPage = () => {
  const { shareToken } = useParams()
  const isOnline = useOnlineStatus()

  const [task, setTask] = useState(null)
  const [meta, setMeta] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [pendingSave, setPendingSave] = useState(null)

  useEffect(() => {
    if (!shareToken) return

    const fetchTask = async () => {
      try {
        setIsLoading(true)
        const response = await taskService.getExternalTask(shareToken)
        setTask(normalizeExternalTask(response.data.data))
        setMeta(response.data.meta)
      } catch (err) {
        const message = err.response?.data?.message || 'Failed to load task'
        setError(message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchTask()
  }, [shareToken])

  // Flush pending save when coming back online
  useEffect(() => {
    if (isOnline && pendingSave) {
      handleSaveForm(pendingSave, true)
      setPendingSave(null)
    }
  }, [isOnline, pendingSave])

  const handleSaveForm = useCallback(async (responses, isAutoSave = false) => {
    if (!isOnline && !isAutoSave) {
      setPendingSave(responses)
      toast.info('You are offline. Changes will be saved when you reconnect.')
      return
    }

    try {
      setSaving(true)
      const response = await taskService.saveExternalFormResponse({
        shareToken,
        responses,
        currentVersion: task?.formResponse?.currentVersion,
      })
      setTask(normalizeExternalTask(response.data.data))
      setLastSavedAt(new Date().toISOString())
      if (!isAutoSave) {
        toast.success('Form saved successfully')
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('This form was updated by someone else. Reloading...')
        const refreshed = await taskService.getExternalTask(shareToken)
        setTask(normalizeExternalTask(refreshed.data.data))
        return
      }
      const message = err.response?.data?.message || 'Failed to save form'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }, [shareToken, isOnline])

  const handleAutoSaveForm = useCallback((responses) => {
    if (!isOnline) {
      setPendingSave(responses)
      return
    }
    handleSaveForm(responses, true)
  }, [handleSaveForm, isOnline])

  const handleSubmitForm = useCallback(async (responses) => {
    try {
      setSaving(true)
      // First save the responses
      await taskService.saveExternalFormResponse({
        shareToken,
        responses,
        currentVersion: task?.formResponse?.currentVersion,
      })
      // Then submit the task
      const response = await taskService.submitExternalTask(shareToken)
      const submittedTask = normalizeExternalTask(response.data.data)
      setTask(submittedTask)
      toast.success(submittedTask.requiresApproval ? 'Task submitted for approval!' : 'Task submitted successfully!')
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to submit task'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }, [shareToken])

  const handleRefreshTask = useCallback(async () => {
    if (!shareToken) return

    const response = await taskService.getExternalTask(shareToken)
    setTask(response.data.data)
    setMeta(response.data.meta)
  }, [shareToken])

  const autoPopulateKeys = useMemo(() => {
    const map = task?.formTemplate?.autoPopulateMap
    if (!map) return []
    return Object.keys(map).map(toSnakeCase)
  }, [task?.formTemplate?.autoPopulateMap])

  if (isLoading) {
    return (
      <PageWrapper>
        <LoadingState>
          <LoadingSpinner />
          <LoadingText>Loading task...</LoadingText>
        </LoadingState>
      </PageWrapper>
    )
  }

  if (error) {
    return (
      <PageWrapper>
        <ErrorState>
          <WarningAmberIcon style={{ fontSize: 48, color: '#faad14' }} />
          <ErrorTitle>Unable to Load Task</ErrorTitle>
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorState>
      </PageWrapper>
    )
  }

  if (!task) {
    return (
      <PageWrapper>
        <ErrorState>
          <WarningAmberIcon style={{ fontSize: 48, color: '#faad14' }} />
          <ErrorTitle>Task Not Found</ErrorTitle>
          <ErrorMessage>This task link may be invalid or expired.</ErrorMessage>
        </ErrorState>
      </PageWrapper>
    )
  }

  const statusBase = taskStatusMeta(task.status || 'assigned')
  const statusInfo = EXTERNAL_STATUS_LABEL[task.status]
    ? { ...statusBase, label: EXTERNAL_STATUS_LABEL[task.status] }
    : statusBase
  const priorityInfo = task.priority ? priorityConfig[task.priority] : null
  const isEditable = ['assigned', 'in_progress'].includes(task.status)
  const isSubmitted = task.status === 'awaiting_approval'
  const isCompleted = task.status === 'completed'
  // Use workingSchema (preserves completed fields when template is updated)
  const schema = task.workingSchema || task.formTemplate?.schema
  const responses = task.formResponse?.responses || {}

  return (
    <PageWrapper>
      <ContentContainer>
        {/* Welcome Banner */}
        <WelcomeBanner>
          <WelcomeText>
            {meta?.name ? `Hello ${meta.name}!` : 'Hello!'} You've been assigned a task
            {task.assignedByName && ` by ${task.assignedByName}`}.
          </WelcomeText>
        </WelcomeBanner>

        {/* Online/Offline Status */}
        {!isOnline && (
          <OfflineBanner>
            <WarningAmberIcon />
            You're offline. Changes will be saved when you reconnect
          </OfflineBanner>
        )}

        {/* Task Header */}
        <HeaderCard>
          <HeaderTop>
            <TaskTitle>{task.title}</TaskTitle>
            <StatusBadge $color={statusInfo.color} $bg={statusInfo.bg}>
              {statusInfo.label}
            </StatusBadge>
          </HeaderTop>

          {task.description && (
            <TaskDescription>{task.description}</TaskDescription>
          )}

          <MetaGrid>
            {task.building?.name && (
              <MetaItem>
                <MetaLabel>Building Passport</MetaLabel>
                <MetaValue>
                  <ApartmentOutlinedIcon />
                  {task.building.name}
                </MetaValue>
              </MetaItem>
            )}

            {priorityInfo && (
              <MetaItem>
                <MetaLabel>Priority</MetaLabel>
                <PriorityBadge $color={priorityInfo.color}>
                  {priorityInfo.label}
                </PriorityBadge>
              </MetaItem>
            )}

            {task.deadline && (
              <MetaItem>
                <MetaLabel>Deadline</MetaLabel>
                <MetaValue $danger={task.isOverdue}>
                  <AccessTimeIcon style={{ fontSize: 16 }} />
                  {moment(task.deadline).format('ddd, DD MMM YYYY')}
                </MetaValue>
              </MetaItem>
            )}

            {meta?.expiresAt && (
              <MetaItem>
                <MetaLabel>Link Expires</MetaLabel>
                <MetaValue>
                  {moment(meta.expiresAt).format('DD MMM YYYY')}
                </MetaValue>
              </MetaItem>
            )}
          </MetaGrid>
        </HeaderCard>

        {/* Form Section */}
        <FormSection>
          {isSubmitted && (
            <SubmittedBanner>
              <CheckCircleOutlineIcon />
              This task has been submitted and is awaiting approval.
            </SubmittedBanner>
          )}

          {isCompleted && (
            <CompletedBanner>
              <CheckCircleOutlineIcon />
              This task has been completed.
            </CompletedBanner>
          )}

          <FormSectionTitle>
            {isEditable && 'Fill in the Form'}
            {isSubmitted && 'Submitted Form'}
            {isCompleted && 'Completed Form'}
          </FormSectionTitle>

          {(isCompleted || task.status === 'awaiting_approval') && <ScoreSummaryCard summary={task.scoreSummary} />}
          {schema ? (
            <FormRenderer
              schema={schema}
              responses={responses}
              onSave={isEditable ? (r) => handleSaveForm(r, false) : undefined}
              onSubmit={isEditable ? handleSubmitForm : undefined}
              onAutoSave={isEditable ? handleAutoSaveForm : undefined}
              lastSavedAt={lastSavedAt}
              isOnline={isOnline}
              readOnly={!isEditable}
              saving={saving}
              taskId={task.id}
              task={task}
              onTaskRefresh={handleRefreshTask}
              autoPopulateKeys={autoPopulateKeys}
            />
          ) : (
            <EmptyMessage>No form available for this task.</EmptyMessage>
          )}
        </FormSection>
      </ContentContainer>
    </PageWrapper>
  )
}

export default ExternalTaskPage

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

const spin = keyframes`
  to { transform: rotate(360deg); }
`

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f7f8fd;
  animation: ${fadeIn} 0.3s ease-out;
`

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 32px 24px 80px;

  @media (max-width: 768px) {
    padding: 20px 16px 80px;
  }
`

const WelcomeBanner = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: white;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
`

const WelcomeText = styled.div`
  font-size: 16px;
  line-height: 1.5;
`

const OfflineBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
  font-size: 14px;
  color: #ad6800;

  svg {
    font-size: 20px;
    color: #faad14;
  }
`

const HeaderCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 28px 32px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`

const TaskTitle = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
  flex: 1;
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color || '#6b7280'};
  background: ${({ $bg }) => $bg || '#f5f7fa'};
  white-space: nowrap;
`

const TaskDescription = styled.p`
  font-size: 14px;
  color: #1a1a2e;
  line-height: 1.6;
  margin: 0 0 20px 0;
`

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
`

const MetaItem = styled.div``

const MetaLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #999;
  margin-bottom: 6px;
`

const MetaValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ $danger }) => ($danger ? '#f5222d' : '#1a1a2e')};

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.6;
  }
`

const PriorityBadge = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $color }) => $color};
`

const FormSection = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 28px 32px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const FormSectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 24px 0;
`

const SubmittedBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  margin-bottom: 24px;
  background: #f9f0ff;
  border: 1px solid #d3adf7;
  border-radius: 8px;
  font-size: 14px;
  color: #722ed1;

  svg {
    font-size: 24px;
  }
`

const CompletedBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  margin-bottom: 24px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  font-size: 14px;
  color: #389e0d;

  svg {
    font-size: 24px;
  }
`

const EmptyMessage = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
`

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e8e8e8;
  border-top-color: #d24724;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

const LoadingText = styled.p`
  margin-top: 16px;
  font-size: 14px;
  color: #999;
`

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
  text-align: center;
`

const ErrorTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 16px 0 8px 0;
`

const ErrorMessage = styled.p`
  font-size: 14px;
  color: #999;
  margin: 0;
  max-width: 400px;
`
